import { VIDEO_CALL_END_REASONS, type VideoCallEndReason } from "@/constants/video-call";

/**
 * Determines if a given disconnect reason indicates a network issue
 * @param reason - The disconnect reason string
 * @param networkStatus - Current network status ('connected' | 'disconnected')
 * @returns true if this is a network-related issue
 */
export const isNetworkIssue = (
  reason: string,
  networkStatus?: 'connected' | 'disconnected'
): boolean => {
  // If network is explicitly disconnected, it's a network issue
  if (networkStatus === 'disconnected') {
    return true;
  }

  // User-initiated actions are not network issues
  if (reason === "USER_INITIATED" || 
      reason === VIDEO_CALL_END_REASONS.USER_ENDED ||
      reason === VIDEO_CALL_END_REASONS.USER_ENDED_DURING_RING) {
    return false;
  }

  // Check for network-related keywords in the reason
  const networkKeywords = [
    'network',
    'connection',
    'timeout',
    'disconnect',
    'reconnect',
    'lost',
    'failed',
    'error',
    'abort',
    'offline',
    'unreachable',
    'socket',
    'websocket',
    'ws',
    'tcp',
    'udp',
    'dns',
    'ssl',
    'tls',
    'certificate',
    'handshake'
  ];

  const reasonLower = reason.toLowerCase();
  const hasNetworkKeyword = networkKeywords.some(keyword => reasonLower.includes(keyword));

  // Specific LiveKit disconnect reasons that indicate network issues
  const networkReasons = [
    'STATE_MISMATCH',
    'JOIN_FAILURE',
    'CONNECTION_FAILED',
    'SERVER_SHUTDOWN',
    'DUPLICATE_IDENTITY',
    'CLIENT_INITIATED',
    'SERVER_SHUTDOWN',
    'PARTICIPANT_REMOVED_BY_SERVER'
  ];

  const isNetworkReason = networkReasons.includes(reason);

  return hasNetworkKeyword || isNetworkReason;
};

/**
 * Gets the appropriate ended reason based on the disconnect reason and network status
 * @param reason - The disconnect reason
 * @param networkStatus - Current network status
 * @returns The appropriate VideoCallEndReason
 */
export const getEndedReasonForDisconnect = (
  reason: string,
  networkStatus?: 'connected' | 'disconnected'
): VideoCallEndReason => {
  // User-initiated endings
  if (reason === "USER_INITIATED") {
    return VIDEO_CALL_END_REASONS.USER_ENDED;
  }

  // Check if it's a network issue
  if (isNetworkIssue(reason, networkStatus)) {
    return VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
  }

  // Specific non-network reasons
  switch (reason) {
    case "PARTICIPANT_REMOVED":
      return VIDEO_CALL_END_REASONS.PARTICIPANT_REMOVED;
    case "ROOM_DELETED":
      return VIDEO_CALL_END_REASONS.ROOM_DELETED;
    default:
      // For any unknown reason during network disconnection, assume network issue
      if (networkStatus === 'disconnected') {
        return VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
      }
      return VIDEO_CALL_END_REASONS.TECHNICAL_ISSUES;
  }
};

/**
 * Ensures that network issues always result in NETWORK_CONNECTION_LOST
 * @param originalReason - The original ended reason
 * @param networkStatus - Current network status
 * @returns The corrected ended reason
 */
export const ensureNetworkEndedReason = (
  originalReason: VideoCallEndReason,
  networkStatus?: 'connected' | 'disconnected'
): VideoCallEndReason => {
  // If network is disconnected and it's not a user action, override to NETWORK_CONNECTION_LOST
  if (networkStatus === 'disconnected' &&
      originalReason !== VIDEO_CALL_END_REASONS.USER_ENDED &&
      originalReason !== VIDEO_CALL_END_REASONS.USER_ENDED_DURING_RING) {
    return VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
  }

  // Check if the original reason suggests a network issue
  const networkRelatedReasons: VideoCallEndReason[] = [
    VIDEO_CALL_END_REASONS.NETWORK_DISCONNECTED,
    VIDEO_CALL_END_REASONS.CONNECTION_FAILED,
    VIDEO_CALL_END_REASONS.TECHNICAL_ISSUES,
    VIDEO_CALL_END_REASONS.SERVER_SHUTDOWN,
    VIDEO_CALL_END_REASONS.ASSISTANT_ENDED // When network is down
  ];

  if (networkRelatedReasons.includes(originalReason) && networkStatus === 'disconnected') {
    return VIDEO_CALL_END_REASONS.NETWORK_CONNECTION_LOST;
  }

  return originalReason;
};